<template>
  <Drawer
    class="sales-order-layer"
    :show="show"
    :props="{
      title: salesOrder?.sales_order_number || $t('projects.sales_order'),
      subtitle: $t('projects.sales_order'),
      icon: 'file-text',
    }"
    @close="$emit('close')"
  >
    <div v-if="loading" class="sales-order-drawer__loading">
      <LoadingSpinner />
    </div>

    <template v-else-if="salesOrder">
      <div class="sales-order-drawer__facts">
        <div>
          <span>{{ $t('projects.status') }}</span>
          <strong>{{ statusLabel }}</strong>
        </div>
        <div v-if="currentTotal != null">
          <span>{{ $t('sales_orders.amount') }}</span>
          <strong>{{ formatCurrency(currentTotal, salesOrder.currency) }}</strong>
        </div>
      </div>

      <button
        v-if="salesOrder.proposal_id"
        type="button"
        class="sales-order-drawer__proposal"
        @click="openProposal"
      >
        {{ $t('projects.proposal') }}
        <BaseIcon name="arrow" size="14" />
      </button>

      <h3 class="sales-order-drawer__heading">{{ $t('sales_orders.specs') }}</h3>
      <ProjectScopeDisplay
        v-if="shownScope"
        :scope="shownScope"
        :format-date-fn="formatDateShort"
      />
      <ul v-else-if="shownLines.length" class="sales-order-drawer__lines">
        <li v-for="(line, index) in shownLines" :key="index">
          <span>{{ line.description || line.name }}</span>
          <span v-if="line.quantity">× {{ line.quantity }}</span>
          <strong v-if="linePrice(line) != null">
            {{ formatCurrency(linePrice(line), salesOrder.currency) }}
          </strong>
        </li>
      </ul>
      <p v-else class="sales-order-drawer__empty">{{ $t('sales_orders.no_specs') }}</p>

      <h3 class="sales-order-drawer__heading">{{ $t('sales_orders.revisions') }}</h3>
      <div class="activity-list">
        <div
          v-for="revision in revisions"
          :key="revision.id"
          class="activity-item"
          :class="[
            `activity-item--${revision.reason}`,
            { 'activity-item--selected': revision.id === shownRevision?.id },
          ]"
          @click="selectedRevisionId = revision.id"
        >
          <div class="activity-marker">
            <div class="activity-dot"></div>
            <div class="activity-line"></div>
          </div>
          <div class="activity-content">
            <div class="activity-timestamp">{{ formatDateShort(revision.created_at) }}</div>
            <div class="activity-text">{{ reasonLabel(revision.reason) }}</div>
            <div v-if="revision.total_amount != null" class="activity-subject">
              {{ formatCurrency(revision.total_amount, revision.currency || salesOrder.currency) }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer v-if="salesOrder?.status === 'confirmed'">
      <Btn variant="danger" :loading="cancelling" @click="handleCancel">
        {{ $t('sales_orders.cancel') }}
      </Btn>
    </template>
  </Drawer>
</template>

<script>
  import { ref, computed, watch, getCurrentInstance } from 'vue';
  import { useRouter } from 'vue-router';
  import { salesOrderService } from '@/services/salesOrderService';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { formatDateShort } from '@/utils/date';
  import { formatCurrency } from '@/utils/currency';
  import { openSalesOrderNotice, salesOrderBlockFromError } from '@/utils/salesOrderNotice';

  import Drawer from '@/components/drawer/Drawer.vue';
  import ProjectScopeDisplay from '@/components/projects/ProjectScopeDisplay.vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';

  export default {
    name: 'SalesOrderDrawer',
    components: { Drawer, Btn, BaseIcon, LoadingSpinner, ProjectScopeDisplay },
    props: {
      show: { type: Boolean, default: false },
      salesOrderId: { type: String, default: null },
    },
    emits: ['close', 'cancelled'],
    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const router = useRouter();
      const uiStore = useUIStore();
      const toastStore = useToastStore();

      const salesOrder = ref(null);
      const loading = ref(false);
      const cancelling = ref(false);
      const selectedRevisionId = ref(null);

      const revisions = computed(() => {
        const rows = salesOrder.value?.revisions || [];
        return [...rows].sort((a, b) => (b.revision_number || 0) - (a.revision_number || 0));
      });

      const shownRevision = computed(() => {
        const rows = revisions.value;
        const selected = rows.find((revision) => revision.id === selectedRevisionId.value);
        return (
          selected ||
          salesOrder.value?.current_revision ||
          rows.find((revision) => revision.id === salesOrder.value?.current_revision_id) ||
          rows[0] ||
          null
        );
      });

      const shownScope = computed(() => {
        const snapshot = shownRevision.value?.enriched_snapshot;
        if (!snapshot || typeof snapshot !== 'object') return null;
        if (snapshot.selectedProduct || Array.isArray(snapshot.products)) return snapshot;
        return null;
      });

      const shownLines = computed(() =>
        (shownRevision.value?.items || []).filter((item) => item && (item.description || item.name))
      );

      const linePrice = (line) => {
        if (line.total != null) return Number(line.total);
        if (line.unit_price == null) return null;
        return Number(line.unit_price) * (Number(line.quantity) || 1);
      };

      const currentTotal = computed(() => {
        const current = salesOrder.value?.current_revision;
        return current?.total_amount ?? null;
      });

      const statusLabel = computed(() => {
        const status = salesOrder.value?.status;
        if (status === 'cancelled') return $t('sales_orders.cancelled');
        return $t('sales_orders.confirmed');
      });

      const reasonLabel = (reason) => {
        const key = `sales_orders.reasons.${reason}`;
        const label = $t(key);
        return label === key ? reason : label;
      };

      const load = async () => {
        if (!props.salesOrderId) {
          salesOrder.value = null;
          return;
        }
        loading.value = true;
        selectedRevisionId.value = null;
        try {
          salesOrder.value = await salesOrderService.getById(props.salesOrderId);
        } catch (error) {
          salesOrder.value = null;
          toastStore.addToast({
            type: 'error',
            message: error?.response?.data?.error || $t('sales_orders.load_failed'),
          });
        } finally {
          loading.value = false;
        }
      };

      watch(
        () => [props.show, props.salesOrderId],
        ([open]) => {
          if (open) load();
        },
        { immediate: true }
      );

      const openProposal = () => {
        if (!salesOrder.value?.proposal_id) return;
        emit('close');
        router.push({ name: 'ProposalDetail', params: { id: salesOrder.value.proposal_id } });
      };

      const handleCancel = async () => {
        let preview;
        try {
          preview = await salesOrderService.cancelPreview(props.salesOrderId);
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: error?.response?.data?.error || $t('sales_orders.cancel_failed'),
          });
          return;
        }
        if (!preview?.applies) return;
        if (!preview.allowed) {
          openSalesOrderNotice(uiStore, preview);
          return;
        }

        const modalId = uiStore.addModal(false, {
          title: preview.title,
          description: preview.description,
          status: 'warning',
          submitText: $t('sales_orders.cancel_confirm'),
          cancelText: $t('sales_orders.cancel_keep'),
          onSubmit: async () => {
            cancelling.value = true;
            try {
              await salesOrderService.cancel(props.salesOrderId);
              uiStore.closeModal(modalId);
              toastStore.addToast({ type: 'success', message: $t('sales_orders.cancelled_done') });
              emit('cancelled');
              emit('close');
            } catch (error) {
              uiStore.closeModal(modalId);
              const blocked = salesOrderBlockFromError(error);
              if (blocked) {
                openSalesOrderNotice(uiStore, blocked);
                return;
              }
              toastStore.addToast({
                type: 'error',
                message: error?.response?.data?.error || $t('sales_orders.cancel_failed'),
              });
            } finally {
              cancelling.value = false;
            }
          },
        });
      };

      return {
        salesOrder,
        loading,
        cancelling,
        revisions,
        shownRevision,
        shownScope,
        shownLines,
        linePrice,
        selectedRevisionId,
        currentTotal,
        statusLabel,
        reasonLabel,
        openProposal,
        handleCancel,
        formatDateShort,
        formatCurrency,
      };
    },
  };
</script>

<style scoped lang="scss">
  .sales-order-layer {
    z-index: 1200;
  }

  .sales-order-drawer {
    &__loading {
      display: flex;
      justify-content: center;
      padding: 2rem;
    }

    &__facts {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
      margin-bottom: 1rem;

      span {
        display: block;
        font-size: 0.75rem;
        color: var(--slate-500);
      }

      strong {
        font-size: 0.9375rem;
        color: var(--slate-900);
      }
    }

    &__proposal {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      margin-bottom: 1.25rem;
      padding: 0;
      border: 0;
      background: none;
      color: var(--brand-600);
      font-size: 0.875rem;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }

    &__heading {
      margin: 1.25rem 0 0.5rem;
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-700);

      &:first-of-type {
        margin-top: 0;
      }
    }

    &__lines {
      margin: 0;
      padding: 0;
      list-style: none;

      li {
        display: flex;
        gap: 0.5rem;
        align-items: baseline;
        padding: 0.375rem 0;
        font-size: 0.875rem;
        color: var(--slate-800);
        border-bottom: 1px solid var(--slate-100);
      }

      span:first-child {
        flex: 1;
        min-width: 0;
      }

      strong {
        font-weight: 600;
      }
    }

    &__empty {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }
  }

  .activity-list {
    display: flex;
    flex-direction: column;
  }

  .activity-item {
    display: flex;
    gap: 0.75rem;
    padding: 0.375rem 0;
    cursor: pointer;

    .activity-marker {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 16px;
      flex-shrink: 0;
    }

    .activity-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--brand-500);
      margin-top: 0.375rem;
    }

    .activity-line {
      flex: 1;
      width: 2px;
      background: var(--slate-200);
      margin-top: 4px;
    }

    &:last-child .activity-line {
      display: none;
    }

    &--selected .activity-text {
      font-weight: 600;
    }

    &--accept .activity-dot {
      background: var(--green-500);
    }

    &--meerwerk .activity-dot,
    &--force_edit .activity-dot,
    &--new_version .activity-dot {
      background: var(--orange-500);
    }

    .activity-timestamp {
      font-size: 0.75rem;
      color: var(--slate-400);
    }

    .activity-text {
      font-size: 0.875rem;
      color: var(--slate-800);
    }

    .activity-subject {
      font-size: 0.8125rem;
      color: var(--slate-500);
    }
  }
</style>
